/**
 * Publish confirmation with a plain-language diff (design 43 AD4).
 *
 * Publishing is the most buyer-visible act in the module, so the dialog states
 * exactly what changes rather than asking "are you sure?". The freshness line
 * is spec 42 R11's 60-second cache window, said in human terms instead of
 * quoting a max-age.
 */
import { ConfirmDialog, Alert } from "@flint/ui";
import { SECTION_LABELS, type HomeSection } from "@flint/api-contract/store-home";

export interface HomeDiff {
  added: string[];
  removed: string[];
  reordered: boolean;
  toggled: string[];
}

/** What changed between the live composition and the one about to go live. */
export function diffSections(live: HomeSection[], next: HomeSection[]): HomeDiff {
  const liveById = new Map(live.map((s) => [s.id, s]));
  const nextById = new Map(next.map((s) => [s.id, s]));

  const added = next.filter((s) => !liveById.has(s.id)).map((s) => SECTION_LABELS[s.type]);
  const removed = live.filter((s) => !nextById.has(s.id)).map((s) => SECTION_LABELS[s.type]);
  const toggled = next
    .filter((s) => liveById.has(s.id) && liveById.get(s.id)!.enabled !== s.enabled)
    .map((s) => `${SECTION_LABELS[s.type]} ${s.enabled ? "shown" : "hidden"}`);

  // Order is only "changed" among the sections that exist on both sides —
  // otherwise adding one section would report everything below it as moved.
  const common = (list: HomeSection[]) =>
    list.filter((s) => liveById.has(s.id) && nextById.has(s.id)).map((s) => s.id);
  const reordered = common(live).join() !== common(next).join();

  return { added, removed, reordered, toggled };
}

export function PublishDialog({
  open,
  onOpenChange,
  diff,
  isFirstPublish,
  busy,
  error,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  diff: HomeDiff;
  isFirstPublish: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
}) {
  const nothingChanged =
    !diff.reordered && !diff.added.length && !diff.removed.length && !diff.toggled.length;

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isFirstPublish ? "Publish your storefront layout" : "Publish these changes"}
      confirmLabel="Publish"
      cancelLabel="Keep editing"
      onConfirm={onConfirm}
      pending={busy}
      footnote="Buyers see this within a minute of publishing."
      description={
        <div className="flex flex-col gap-3 text-sm">
        {error ? (
          <Alert intent="bad" title="Couldn't publish">
            {error}
          </Alert>
        ) : null}

        {isFirstPublish ? (
          <p className="text-muted">
            Your store currently shows the default layout. Publishing replaces it with this one.
          </p>
        ) : nothingChanged ? (
          <p className="text-muted">
            Section settings changed, but nothing was added, removed, hidden or moved.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {diff.added.map((label) => (
              <li key={`a-${label}`} className="text-ok">
                + {label} added
              </li>
            ))}
            {diff.removed.map((label) => (
              <li key={`r-${label}`} className="text-bad">
                − {label} removed
              </li>
            ))}
            {diff.toggled.map((label) => (
              <li key={`t-${label}`} className="text-muted">
                • {label}
              </li>
            ))}
            {diff.reordered ? <li className="text-muted">↕ Sections reordered</li> : null}
          </ul>
        )}

        </div>
      }
    />
  );
}
